import HorizontalStraightBorders from '@components/shapes/HorizontalStraightBorders'
import VerticalBorders from '@components/shapes/VerticalBorders'
import { minWidthQueries } from '@operations/utils/useMediaQuery'
import Link from 'next/link'
import React, { useState } from 'react'
import ProgressiveImage from 'react-progressive-image'
import styled, { useTheme } from 'styled-components'

const StyledMediaWrap = styled.div`
  overflow: hidden;
  height: 165px;
  width: 165px;
  position: relative;
  cursor: ${({ theme }) => theme.cursor.pointer};
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-position: center;
  transition: all 0.2s;
  svg {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }
  @media ${minWidthQueries.md} {
    height: 225px;
    width: 225px;
    max-width: 100%;
  }
  @media (max-width: 768px) {
    height: 225px;
    width: 225px;
  }
`
const StyledSentiusCard = styled.a`
  position: relative;
  width: ${({ width }) => width || '100%'};
  padding: ${({ padding }) => padding || '5px'};

  aspect-ratio: 1 / 1;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: all 0.2s;
  ${HorizontalStraightBorders} {
    &:before,
    &:after {
      width: 100%;
    }
  }
  ${VerticalBorders} {
    &:before,
    &:after {
      height: 100%;
    }
  }
  &:hover {
    cursor: ${({ theme }) => theme.cursor.default};
    transform: translateY(-5px);
    ${HorizontalStraightBorders} {
      &:before,
      &:after {
        background-color: ${props => props.firstElemColor};
        width: calc(100% - 30px);
      }
    }
    ${VerticalBorders} {
      &:before,
      &:after {
        background-color: ${props => props.secondElemColor};
        height: calc(100% - 30px);
      }
    }
    ${StyledMediaWrap} {
      border-radius: ${({ theme }) => theme.borderRadius * 2}px;
    }
  }
`
const StyledSentiusImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius * 2}px;
  object-fit: cover;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`
const StyledSentiusBio = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0;
  cursor: ${({ theme }) => theme.cursor.pointer};
  @media ${minWidthQueries.md} {
    padding: 0.5rem;
  }
`
const SentiusNumber = styled.div`
  color: ${({ theme }) => theme.colors.grey};
  align-items: center;
  justify-content: center;
  display: flex;
  margin-right: 0.25rem;
`
const SentiusName = styled.div`
  color: ${({ theme }) => theme.colors.grey};
  align-items: center;
  justify-content: center;
  display: flex;
  margin-right: 0.25rem;
`
const SentiusElement = styled.div`
  color: ${({ theme }) => theme.colors.grey};
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  margin-right: 0.125rem;
  position: relative;
  img {
    width: 28px;
  }
`

const SentiusCard = ({
  slug,
  number,
  name,
  elements,
  width,
  cardImage,
  dynastyFilter,
  padding,
}) => {
  const theme = useTheme()
  if (!slug || !number || !name) return null
  const sentiusElements = elements
  const firstElemColor = elements[0]?.colors?.primary || theme.colors.grey
  const secondElemColor = elements[1]?.colors?.primary
    ? elements[1]?.colors?.primary
    : firstElemColor

  return (
    <Link
      key={`${number}_${slug}`}
      passHref
      href={`/sentipedia/${slug}${dynastyFilter ? `?dynastyFilter=${dynastyFilter}` : ''}`}
    >
      <StyledSentiusCard
        firstElemColor={firstElemColor}
        padding={padding}
        secondElemColor={secondElemColor}
        width={width}
      >
        <StyledMediaWrap backgroundImage={theme.images.sentiusPattern}>
          <HorizontalStraightBorders height="5px" />
          <VerticalBorders width="5px" />
          {cardImage && (
            <ProgressiveImage
              key={cardImage.id}
              placeholder={cardImage?.formats?.thumbnail?.url}
              src={cardImage?.formats?.small?.url}
              style={{
                height: cardImage?.formats?.small?.height,
                width: cardImage?.formats?.small?.width,
              }}
            >
              {(src, loading) => (
                <StyledSentiusImg
                  key={cardImage.id}
                  alt={cardImage.alternativeText}
                  src={src}
                  style={{ filter: loading ? 'blur(2px)' : '' }}
                />
              )}
            </ProgressiveImage>
          )}
        </StyledMediaWrap>
        <StyledSentiusBio>
          {sentiusElements?.map(element => {
            return (
              <SentiusElement key={element.id}>
                <ProgressiveImage
                  placeholder={element?.icon?.formats?.thumbnail?.url}
                  src={element?.icon?.formats?.small?.url}
                  style={{
                    height: element?.icon?.formats?.thumbnail?.height,
                    width: element?.icon?.formats?.thumbnail?.width,
                  }}
                >
                  {(src, loading) => (
                    <img
                      alt={element.name}
                      src={src}
                      style={{ filter: loading ? 'blur(2px)' : '' }}
                    />
                  )}
                </ProgressiveImage>
              </SentiusElement>
            )
          })}
          <SentiusNumber>#{number}</SentiusNumber>
          <SentiusName>{name}</SentiusName>
        </StyledSentiusBio>
      </StyledSentiusCard>
    </Link>
  )
}

export default SentiusCard
